import { Component, OnInit, ViewChild, ElementRef, Renderer2, PipeTransform, OnDestroy } from '@angular/core';

import { environment } from '../../../environments/environment';
import { Router, ActivatedRoute } from '@angular/router';
import { MotNaoCompra } from '../models/mot-nao-compra';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MotNaoCompraService } from './mot-nao-compra.service';
import { Observable, Subject } from 'rxjs';
import { LoaderService } from 'src/app/loader/loader.service';
import * as moment from 'moment';
import { FormControl } from '@angular/forms';
import { startWith, map, takeUntil } from 'rxjs/operators';
import { DecimalPipe } from '@angular/common';
import { PermissionsService } from 'src/app/services/permissions.service';

@Component({
  selector: 'app-mot-nao-compra',
  templateUrl: './mot-nao-compra.component.html',
  styleUrls: ['./mot-nao-compra.component.scss'],
})
export class MotNaoCompraComponent implements OnInit, OnDestroy {
  env;
  modalActive: NgbModalRef;
  motsNaoCompra: MotNaoCompra[];
  motsNaoCompra$: Observable<MotNaoCompra[]>;
  motNaoCompra: MotNaoCompra;

  page = 1;
  pageSize = 10;
  collectionSize = 0;
  filter = new FormControl('');

  timeoutIDSuccess;
  timeoutIDError;

  private unsubscribe$ = new Subject();

  @ViewChild('alertSuccess', { read: ElementRef, static: false })
  alertSuccess: ElementRef;

  @ViewChild('alertError', { read: ElementRef, static: false })
  alertError: ElementRef;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private motService: MotNaoCompraService,
    private renderer: Renderer2,
    private loaderService: LoaderService,
    public permissionsService: PermissionsService
  ) {
    this.env = environment;
  }

  ngOnInit() {
    this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
      if (this.permissionsService.isPermitted('settings')) {
        this.getMotsNaoComp(this.env.selected.cnpj);
      } else {
        this.router.navigateByUrl(`/dash/${params.cnpj}`);
      }
    });
  }

  async getMotsNaoComp(cnpj: string) {
    const loader = await this.loaderService.notifica({
      title: 'Buscando motivos...',
      date: moment().toISOString(),
      type: 'loading',
    });

    this.motsNaoCompra = await this.motService.getMotsNaoComp(cnpj).toPromise();

    this.motsNaoCompra$ = this.filter.valueChanges.pipe(
      startWith(''),
      map(text => this.search(text, new DecimalPipe('pt')))
    );

    loader.dismiss();
  }

  onSubmit(isNew: boolean) {
    if (isNew) {
      this.addMotNaoComp(this.motNaoCompra);
    } else {
      this.updateMotNaoComp(this.motNaoCompra._id, this.motNaoCompra);
    }
    this.modalActive.close();
  }

  addSuccessAlert(msg: string) {
    clearTimeout(this.timeoutIDSuccess);

    this.renderer.setProperty(this.alertSuccess.nativeElement, 'innerHTML', msg);
    this.renderer.setProperty(this.alertSuccess.nativeElement, 'hidden', false);

    this.timeoutIDSuccess = setTimeout(_ => {
      this.renderer.setProperty(this.alertSuccess.nativeElement, 'hidden', true);
    }, 10000);
  }

  addErrorAlert(msg: string) {
    clearTimeout(this.timeoutIDError);

    this.renderer.setProperty(this.alertError.nativeElement, 'innerHTML', msg);
    this.renderer.setProperty(this.alertError.nativeElement, 'hidden', false);

    this.timeoutIDError = setTimeout(_ => {
      this.renderer.setProperty(this.alertError.nativeElement, 'hidden', true);
    }, 10000);
  }

  async addMotNaoComp(motNaoComp: MotNaoCompra) {
    const loader = await this.loaderService.notifica({
      title: 'Salvando novo motivo...',
      date: moment().toISOString(),
      type: 'loading',
    });

    const newMotNaoComp = await this.motService.addMotNaoComp(motNaoComp).toPromise();

    if ('error' in newMotNaoComp) {
      this.addErrorAlert(newMotNaoComp.error);
    } else {
      this.addSuccessAlert(`Motivo ${newMotNaoComp.texto} cadastrado com sucesso.`);
      this.getMotsNaoComp(motNaoComp.cnpj);
    }

    loader.dismiss();
  }

  async updateMotNaoComp(id: string, motNaoComp: MotNaoCompra) {
    const loader = await this.loaderService.notifica({
      title: 'Atualizando motivo...',
      date: moment().toISOString(),
      type: 'loading',
    });

    const updedMotNaoComp = await this.motService.updateMotNaoComp(id, motNaoComp).toPromise();

    if ('error' in updedMotNaoComp) {
      this.addErrorAlert(updedMotNaoComp.error);
    } else {
      this.addSuccessAlert(`Motivo ${updedMotNaoComp.texto} atualizado com sucesso.`);
      this.getMotsNaoComp(motNaoComp.cnpj);
    }

    loader.dismiss();
  }

  motNovo(modal) {
    this.motNaoCompra = { cnpj: this.env.selected.cnpj, codigo: null, texto: '', new: true };
    this.modalActive = this.modalService.open(modal, { size: 'lg' });
  }

  motEdit(motivo: MotNaoCompra, modal) {
    this.motNaoCompra = { ...motivo, edit: true };
    this.modalActive = this.modalService.open(modal, { size: 'lg' });
  }

  search(text: string, pipe: PipeTransform): MotNaoCompra[] {
    const motsFiltered: MotNaoCompra[] = this.motsNaoCompra.filter(motivo => {
      const term = text.toLowerCase();
      return pipe.transform(motivo.codigo).includes(term) || motivo.texto.toLowerCase().includes(term);
    });

    this.page = 1;
    this.collectionSize = motsFiltered.length;

    return motsFiltered;
  }

  goBack(): void {
    this.router.navigateByUrl(`/settings/${this.env.selected.cnpj}`);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
